import { QuestionResponseField } from "@/shared/components/QuestionResponseField";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

interface IntakeFormQuestionItemProps {
  index: number;
  isInvalid?: boolean;
  onFocus?: (questionId: string) => void;
  questionRef?: React.MutableRefObject<Map<string, HTMLDivElement>>;
  onFileUploadError?: (questionIndex: number, hasError: boolean) => void;
  onFileUploadStatusChange?: (questionIndex: number, isUploading: boolean) => void;
}

export const IntakeFormQuestionItem: React.FC<IntakeFormQuestionItemProps> = ({
  index,
  isInvalid = false,
  onFocus,
  questionRef,
  onFileUploadError,
  onFileUploadStatusChange,
}) => {
  const { formState: { errors }, watch, trigger } = useFormContext();

  // Get question data from form context
  const question = watch(`questions.${index}`);
  const response = watch(`questions.${index}.response`);

  const itemRef = useRef<HTMLDivElement>(null);
  const questionKey = String(question?.question_number);

  // Register this question's ref
  useEffect(() => {
    if (questionRef && itemRef.current && questionKey) {
      questionRef.current.set(questionKey, itemRef.current);
      return () => {
        questionRef.current.delete(questionKey);
      };
    }
  }, [questionRef, questionKey]);

  // Error path for field array
  const questionErrors = errors?.questions as any;
  const questionError = questionErrors?.[index];
  const hasError = isInvalid || !!questionError?.response;

  // Trigger validation on response change if error exists
  useEffect(() => {
    // Only trigger if there's already an error for this question
    if (hasError && response !== undefined) {
      trigger(`questions.${index}.response`);
    }
  }, [response, hasError, index, trigger]);

  const handleFocus = useCallback(() => {
    if (onFocus && questionKey) {
      onFocus(questionKey);
    }
  }, [onFocus, questionKey]);

  const handleFileUploadError = useCallback((hasError: boolean) => {
    if (onFileUploadError) {
      onFileUploadError(index, hasError);
    }
  }, [onFileUploadError, index]);

  const handleFileUploadStatusChange = useCallback((isUploading: boolean) => {
    if (onFileUploadStatusChange) {
      onFileUploadStatusChange(index, isUploading);
    }
  }, [onFileUploadStatusChange, index]);

  if (!question) return null;

  return (
    <div
      ref={itemRef}
      className={`flex flex-col gap-4 transition-all`}
    >
      <QuestionResponseField
        index={index}
        onFocus={handleFocus}
        onFileUploadError={handleFileUploadError}
        onFileUploadStatusChange={handleFileUploadStatusChange}
      />
    </div>
  );
};
